import {useEffect, useState} from "react";
import {FilterButton, PhoneHQGlyph, PhoneHQAvatar, PhoneHQButton, PhoneHQCheckbox, PhoneHQDatepicker, PhoneHQInput} from 'ch3-ui-lib';
import {Calendar, ChevronDown, ChevronUp, CircleDot, Phone, PhoneCall, Search, ShieldCheck, Trash2, Users, UsersRound, X} from "lucide-react";
import moment from "moment";
import {useUsersFilter} from "./Users.tsx";
import {usePhoneNumbersFilter} from "./PhoneNumbers.tsx";
import {useContactFilters} from "./Contacts.tsx";
import {useGroupFilters} from "./Groups.tsx";
import type {GroupListItem} from "../../store/features/groups/groupsService.ts";
import {getFullName} from "../../utilis/getFullName.ts";

type FilterKey = "USERS" | "CONTACTS" | "DATE" | "PHONE_NUMBERS" | "CALL_TYPE" | "GROUPS" | "ROLES" | "STATUS";
type EntityKey = "USERS" | "CONTACTS" | "PHONE_NUMBERS" | "GROUPS";

interface DateRange {
    from: string;
    to: string;
}

interface CallFiltersPanelProps {
    items: FilterKey[];
    admin?: boolean;
    handlers: {
        onUsersChange?: (users: any[]) => void;
        onContactsChange?: (contacts: any[]) => void;
        onPhoneNumbersChange?: (numbers: any[]) => void;
        onCallTypesChange?: (types: string[]) => void;
        handleDateSelect?: (range: DateRange) => void;
        onGroupsChange?: (groups: GroupListItem[]) => void;
        onRolesChange?: (roles: string[]) => void;
        onStatusChange?: (status: string[]) => void;
    };
    selectedGroups?: GroupListItem[];
    selectedRoles?: string[];
    selectedStatus?: string[];
    selectedUsers?: any[];
    selectedContacts?: any[];
    selectedPhoneNumbers?: any[];
    selectedCallTypes?: string[];
    selectedDates?: DateRange;
    callTypeCounts?: Record<string, number>;
    /** 36px icon-only trigger, as in list panels. */
    iconOnly?: boolean;
}

const defaultFrom = () => moment().subtract(100, "years").startOf("day").toISOString();
const defaultTo = () => moment().add(100, "years").endOf("day").toISOString();

// The "all time" sentinel spans ~200 years — treat any range that wide as "no date filter".
const isDateActive = (range?: DateRange | null) =>
    !!range?.from && !!range?.to && moment(range.to).diff(moment(range.from), "years") < 150;

const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});

const SECTION_META: Record<FilterKey, {label: string; icon: JSX.Element}> = {
    USERS: {label: "Users", icon: <Users size={18} className="text-ink-muted"/>},
    CONTACTS: {label: "Contacts", icon: <Users size={18} className="text-ink-muted"/>},
    DATE: {label: "Date", icon: <Calendar size={18} className="text-ink-muted"/>},
    PHONE_NUMBERS: {label: "Phone numbers", icon: <Phone size={18} className="text-ink-muted"/>},
    CALL_TYPE: {label: "Call type", icon: <PhoneCall size={18} className="text-ink-muted"/>},
    GROUPS: {label: "Groups", icon: <UsersRound size={18} className="text-ink-muted"/>},
    ROLES: {label: "Role", icon: <ShieldCheck size={18} className="text-ink-muted"/>},
    STATUS: {label: "Status", icon: <CircleDot size={18} className="text-ink-muted"/>},
};

const ROLE_OPTIONS = [{label: "Admin", value: "ADMIN"}, {label: "User", value: "USER"}];
const STATUS_OPTIONS = [{label: "Active", value: "ACTIVE"}, {label: "Inactive", value: "DELETED"}];

const CALL_TYPE_OPTIONS: {label: string; value: string}[] = [
    {label: "Incoming call", value: "INCOMING"},
    {label: "Outgoing call", value: "OUTGOING"},
    {label: "Forwarded call", value: "FORWARDED"},
];

export default function CallFiltersPanel({
                                             items,
                                             admin = false,
                                             handlers,
                                             selectedUsers = [],
                                             selectedContacts = [],
                                             selectedPhoneNumbers = [],
                                             selectedCallTypes = [],
                                             selectedDates,
                                             callTypeCounts,
                                             iconOnly = false,
                                             selectedGroups = [],
                                             selectedRoles = [],
                                             selectedStatus = [],
                                         }: CallFiltersPanelProps) {
    const {onUsersChange, onContactsChange, onPhoneNumbersChange, onCallTypesChange, handleDateSelect, onGroupsChange, onRolesChange, onStatusChange} = handlers;

    const [open, setOpen] = useState(false);
    const [expandedSection, setExpandedSection] = useState<FilterKey | null>(null);

    // Local draft — committed to the parent only on Apply.
    const [draftUsers, setDraftUsers] = useState<any[]>([]);
    const [draftContacts, setDraftContacts] = useState<any[]>([]);
    const [draftPhones, setDraftPhones] = useState<any[]>([]);
    const [draftCallTypes, setDraftCallTypes] = useState<string[]>([]);
    const [draftDate, setDraftDate] = useState<DateRange | null>(null);
    const [draftGroups, setDraftGroups] = useState<GroupListItem[]>([]);
    const [draftRoles, setDraftRoles] = useState<string[]>([]);
    const [draftStatus, setDraftStatus] = useState<string[]>([]);
    const [groupsSearch, setGroupsSearch] = useState("");
    const [datepickerKey, setDatepickerKey] = useState(0);

    const [usersSearch, setUsersSearch] = useState("");
    const [contactsSearch, setContactsSearch] = useState("");
    const [phonesSearch, setPhonesSearch] = useState("");
    const [focusedEntity, setFocusedEntity] = useState<EntityKey | null>(null);

    const {users, setUsersPhrase, handleUserSelect} = useUsersFilter({
        selectedUsers: draftUsers,
        setSelectedUsers: setDraftUsers,
        admin,
    });

    const {contacts, setContactsPhrase, handleContactSelect, onScrollEnd: onContactsScrollEnd} = useContactFilters({
        selectedContacts: draftContacts,
        setSelectedContacts: setDraftContacts,
        admin,
    });

    const {phoneNumbers, setPhonePhrase, handlePhoneNumberSelect} = usePhoneNumbersFilter({
        selectedNumbers: draftPhones,
        setSelectedNumbers: setDraftPhones,
        admin,
    });

    const {groups, setGroupPhrase, handleGroupSelect, onScrollEnd: onGroupsScrollEnd} = useGroupFilters({
        selectedGroups: draftGroups,
        setSelectedGroups: setDraftGroups,
        admin,
    });

    useEffect(() => {
        setGroupPhrase(groupsSearch);
    }, [groupsSearch, setGroupPhrase]);

    useEffect(() => {
        setUsersPhrase(usersSearch);
    }, [usersSearch, setUsersPhrase]);

    useEffect(() => {
        setContactsPhrase(contactsSearch);
    }, [contactsSearch, setContactsPhrase]);

    useEffect(() => {
        setPhonePhrase(phonesSearch);
    }, [phonesSearch, setPhonePhrase]);

    // Sync the draft with the applied filters whenever the panel opens.
    useEffect(() => {
        if (!open) return;
        setDraftUsers(selectedUsers ?? []);
        setDraftContacts(selectedContacts ?? []);
        setDraftPhones(selectedPhoneNumbers ?? []);
        setDraftCallTypes(selectedCallTypes ?? []);
        setDraftDate(isDateActive(selectedDates) ? selectedDates! : null);
        setDraftGroups(selectedGroups ?? []);
        setDraftRoles(selectedRoles ?? []);
        setDraftStatus(selectedStatus ?? []);
        setGroupsSearch("");
        setUsersSearch("");
        setContactsSearch("");
        setPhonesSearch("");
        setExpandedSection(null);
        setDatepickerKey(k => k + 1);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleSection = (key: FilterKey) =>
        setExpandedSection(prev => (prev === key ? null : key));

    const isUserSelected = (value: any) => draftUsers.some(u => u.id === value.id);
    const isContactSelected = (value: any) => draftContacts.some(c => c.contactId === value.contactId);
    const isPhoneSelected = (value: any) => draftPhones.some(n => n.msisdnId === value.msisdnId);
    const isGroupSelected = (value: Pick<GroupListItem, "groupId">) => draftGroups.some(g => g.groupId === value.groupId);
    const toggleIn = (set: (fn: (prev: string[]) => string[]) => void) => (value: string) =>
        set(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));

    const handleDateConfirm = (dates: any[], presetLabel?: string) => {
        if (presetLabel === "All time" || !dates || dates.length !== 2) {
            setDraftDate(null);
            return;
        }
        const from = dates[0].toDate ? dates[0].toDate().toISOString() : new Date(dates[0]).toISOString();
        const to = dates[1].toDate ? dates[1].toDate().toISOString() : new Date(dates[1]).toISOString();
        setDraftDate({from, to});
    };

    const resetDate = () => {
        setDraftDate(null);
        setDatepickerKey(k => k + 1);
    };

    const toggleCallType = (value: string) =>
        setDraftCallTypes(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));

    const sectionHasSelection = (key: FilterKey) => {
        switch (key) {
            case "USERS": return draftUsers.length > 0;
            case "CONTACTS": return draftContacts.length > 0;
            case "PHONE_NUMBERS": return draftPhones.length > 0;
            case "CALL_TYPE": return draftCallTypes.length > 0;
            case "DATE": return draftDate !== null;
            case "GROUPS": return draftGroups.length > 0;
            case "ROLES": return draftRoles.length > 0;
            case "STATUS": return draftStatus.length > 0;
        }
    };

    const resetSection = (key: FilterKey) => {
        switch (key) {
            case "USERS": setDraftUsers([]); break;
            case "CONTACTS": setDraftContacts([]); break;
            case "PHONE_NUMBERS": setDraftPhones([]); break;
            case "CALL_TYPE": setDraftCallTypes([]); break;
            case "DATE": resetDate(); break;
            case "GROUPS": setDraftGroups([]); break;
            case "ROLES": setDraftRoles([]); break;
            case "STATUS": setDraftStatus([]); break;
        }
    };

    const handleClear = () => {
        setDraftUsers([]);
        setDraftContacts([]);
        setDraftPhones([]);
        setDraftCallTypes([]);
        resetDate();
        setDraftGroups([]);
        setDraftRoles([]);
        setDraftStatus([]);
        setGroupsSearch("");
        setUsersSearch("");
        setContactsSearch("");
        setPhonesSearch("");
    };

    const handleCancel = () => setOpen(false);

    const handleApply = () => {
        onUsersChange?.(draftUsers);
        onContactsChange?.(draftContacts);
        onPhoneNumbersChange?.(draftPhones);
        onCallTypesChange?.(draftCallTypes);
        if (items.includes("DATE")) handleDateSelect?.(draftDate ?? {from: defaultFrom(), to: defaultTo()});
        onGroupsChange?.(draftGroups);
        onRolesChange?.(draftRoles);
        onStatusChange?.(draftStatus);
        setOpen(false);
    };

    // One per active filter category, matching the chips shown next to the button.
    const appliedCount = [
        selectedUsers?.length,
        selectedContacts?.length,
        selectedPhoneNumbers?.length,
        selectedCallTypes?.length,
        isDateActive(selectedDates) ? 1 : 0,
        selectedGroups?.length,
        selectedRoles?.length,
        selectedStatus?.length,
    ].filter(Boolean).length;

    const dateLabel = draftDate
        ? `${formatDate(draftDate.from)} - ${formatDate(draftDate.to)}`
        : "All time";

    // ----- Entity sections (Users / Contacts / Phone numbers) -----

    const entitySource = (key: EntityKey) => {
        switch (key) {
            case "USERS":
                return {
                    search: usersSearch, setSearch: setUsersSearch, placeholder: "Add users...",
                    results: users, selected: draftUsers,
                    isSelected: isUserSelected, toggle: handleUserSelect,
                    keyOf: (v: any) => `u-${v.id}`,
                };
            case "CONTACTS":
                return {
                    search: contactsSearch, setSearch: setContactsSearch, placeholder: "Add contacts...",
                    results: contacts, selected: draftContacts,
                    isSelected: isContactSelected, toggle: handleContactSelect,
                    keyOf: (v: any) => `c-${v.contactId}`,
                    onScrollEnd: onContactsScrollEnd,
                };
            case "PHONE_NUMBERS":
                return {
                    search: phonesSearch, setSearch: setPhonesSearch, placeholder: "Add phone numbers...",
                    results: phoneNumbers, selected: draftPhones,
                    isSelected: isPhoneSelected, toggle: handlePhoneNumberSelect,
                    keyOf: (v: any) => `p-${v.msisdnId}`,
                };
            case "GROUPS":
                return {
                    search: groupsSearch, setSearch: setGroupsSearch, placeholder: "Add groups...",
                    results: groups, selected: draftGroups,
                    isSelected: isGroupSelected, toggle: handleGroupSelect,
                    keyOf: (v: Pick<GroupListItem, "groupId">) => `g-${v.groupId}`,
                    onScrollEnd: onGroupsScrollEnd,
                };
        }
    };

    const labelOf = (key: EntityKey, value: any) =>
        key === "PHONE_NUMBERS" ? value.label : key === "GROUPS" ? value.name : getFullName(value.firstName, value.lastName);

    const renderAvatar = (key: EntityKey, value: any, big: boolean) => {
        if (key === "PHONE_NUMBERS" || key === "GROUPS") {
            return (
                <span
                    className={`flex items-center justify-center ${big ? "size-7 rounded-lg" : "size-5 rounded-md"} text-white shrink-0`}
                    style={{backgroundColor: value.iconColor || "#a78bfa"}}
                >
                    <PhoneHQGlyph name={value.icon || (key === "GROUPS" ? "group" : "call")} size={20}/>
                </span>
            );
        }
        return (
            <PhoneHQAvatar
                size={big ? "28px" : "20px"}
                type={value.avatarUrl ? "image" : "initials"}
                src={value.avatarUrl}
                name={getFullName(value.firstName, value.lastName)}
                colorFromName
            />
        );
    };

    const renderEntitySection = (key: EntityKey) => {
        const {search, setSearch, placeholder, results, selected, isSelected, toggle, keyOf, onScrollEnd} =
            entitySource(key) as ReturnType<typeof entitySource> & {onScrollEnd?: () => void};

        const available = results.filter((item: any) => !isSelected(item.value));
        const showResults = (focusedEntity === key || search.length > 0) && available.length > 0;

        return (
            <div className="pl-[68px] pr-5 pb-5">
                {/* Search field */}
                <div className="relative">

                    <PhoneHQInput
                        prefixIcon={ <Search size={18} className="!p-0 text-ink-muted pointer-events-none"/>}
                        placeholder={placeholder}
                        className="pl-9 w-full h-[36px]"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onFocus={() => setFocusedEntity(key)}
                        onBlur={() => setFocusedEntity(prev => (prev === key ? null : prev))}
                    />
                </div>

                {/* Search results — dark dropdown matching the Contacts selection */}
                {showResults && (
                    <div
                        className="bg-zinc-950 rounded-2xl px-1 py-1 mt-1 max-h-[280px] overflow-y-auto shadow-[0px_20px_5px_0px_rgba(0,0,0,0),0px_13px_5px_0px_rgba(0,0,0,0.03),0px_7px_4px_0px_rgba(0,0,0,0.09),0px_3px_3px_0px_rgba(0,0,0,0.15),0px_1px_2px_0px_rgba(0,0,0,0.17)]"
                        onScroll={(e) => {
                            if (!onScrollEnd) return;
                            const el = e.currentTarget;
                            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) onScrollEnd();
                        }}
                    >
                        {available.map((item: any) => (
                            <div
                                key={item.key}
                                className="flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer hover:bg-surface/[0.06]"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => toggle(item.value)}
                            >
                                {renderAvatar(key, item.value, false)}
                                <span className="text-footnote font-semibold text-white truncate">
                                    {labelOf(key, item.value)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Selected items */}
                {selected.length > 0 && (
                    <div className="flex flex-col">
                        {selected.map((value: any) => (
                            <div
                                key={keyOf(value)}
                                className="flex items-center gap-3 py-4 border-b border-line last:border-b-0"
                            >
                                {renderAvatar(key, value, true)}
                                <span className="flex-1 text-footnote font-medium text-ink truncate">
                                    {labelOf(key, value)}
                                </span>
                                <button
                                    onClick={() => toggle(value)}
                                    className="flex items-center justify-center size-7 rounded-control hover:bg-surface-muted cursor-pointer"
                                >
                                    <Trash2 size={16} className="!p-0 text-ink-muted"/>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderCallTypeSection = () => (
        <div className="pl-[68px] pr-7 pb-5">
            <div className="flex flex-col gap-5">
                {CALL_TYPE_OPTIONS.map(option => {
                    const count = callTypeCounts?.[option.value];
                    const disabled = count === 0;
                    return (
                        <label
                            key={option.value}
                            className={`flex items-center gap-[10px] ${disabled ? "cursor-default" : "cursor-pointer"}`}
                        >
                            <PhoneHQCheckbox
                                value={option.value}
                                checked={draftCallTypes.includes(option.value)}
                                disabled={disabled}
                                onCheckedChange={() => !disabled && toggleCallType(option.value)}
                            />
                            <span className={`flex-1 text-footnote font-medium leading-4 truncate ${disabled ? "text-ink-muted" : "text-ink"}`}>
                                {option.label}
                            </span>
                            {typeof count === "number" && (
                                <span className={`text-footnote font-medium leading-4 ${disabled ? "text-ink-muted" : "text-ink-muted"}`}>
                                    {count}
                                </span>
                            )}
                        </label>
                    );
                })}
            </div>
        </div>
    );

    const renderOptionSection = (options: {label: string; value: string}[], selected: string[], toggle: (value: string) => void) => (
        <div className="pl-[68px] pr-7 pb-5">
            <div className="flex flex-col gap-5">
                {options.map(option => (
                    <label key={option.value} className="flex cursor-pointer items-center gap-[10px]">
                        <PhoneHQCheckbox value={option.value} checked={selected.includes(option.value)} onCheckedChange={() => toggle(option.value)}/>
                        <span className="flex-1 truncate text-footnote font-medium leading-4 text-ink">{option.label}</span>
                    </label>
                ))}
            </div>
        </div>
    );

    // The footer shows a Clear button only when at least one section has a draft selection.
    const hasAnySelection =
        draftUsers.length > 0 || draftContacts.length > 0 || draftPhones.length > 0 ||
        draftCallTypes.length > 0 || draftDate !== null ||
        draftGroups.length > 0 || draftRoles.length > 0 || draftStatus.length > 0;

    return (
        <>
            <FilterButton count={appliedCount} iconOnly={iconOnly} onClick={() => setOpen(true)}/>

            {open && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-scrim" onClick={handleCancel}/>

                    {/* Panel */}
                    <div className="relative bg-surface rounded-l-[20px] w-[640px] max-w-[90vw] flex flex-col shadow-overlay overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-line pl-5 pr-3 py-3.5">
                            <span className="text-sm font-semibold text-ink">Filters</span>
                            <button
                                onClick={handleCancel}
                                className="flex items-center justify-center size-7 rounded-control hover:bg-surface-muted cursor-pointer"
                            >
                                <X size={18} className="!p-0 text-ink-muted"/>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto">
                            {items.map(key => {
                                const meta = SECTION_META[key];
                                if (!meta) return null;
                                const isExpanded = expandedSection === key;
                                const hasSelection = sectionHasSelection(key);

                                return (
                                    <div key={key} className="border-b border-line">
                                        <div
                                            className="flex items-center gap-4 pl-5 pr-3 py-3.5 cursor-pointer"
                                            onClick={() => toggleSection(key)}
                                        >
                                            <div className="bg-surface-muted rounded-control size-9 flex items-center justify-center shrink-0">
                                                {meta.icon}
                                            </div>
                                            <span className="flex-1 text-sm font-medium text-ink truncate">
                                                {meta.label}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {hasSelection && (
                                                    <PhoneHQButton
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            resetSection(key);
                                                        }}
                                                    >
                                                        Reset
                                                    </PhoneHQButton>
                                                )}
                                                <div className="flex items-center justify-center size-9 rounded-control">
                                                    {isExpanded
                                                        ? <ChevronUp size={20} className="text-ink-muted"/>
                                                        : <ChevronDown size={20} className="text-ink-muted"/>}
                                                </div>
                                            </div>
                                        </div>

                                        {isExpanded && (key === "USERS" || key === "CONTACTS" || key === "PHONE_NUMBERS" || key === "GROUPS") &&
                                            renderEntitySection(key)}

                                        {isExpanded && key === "ROLES" && renderOptionSection(ROLE_OPTIONS, draftRoles, toggleIn(setDraftRoles))}
                                        {isExpanded && key === "STATUS" && renderOptionSection(STATUS_OPTIONS, draftStatus, toggleIn(setDraftStatus))}

                                        {isExpanded && key === "CALL_TYPE" && renderCallTypeSection()}

                                        {isExpanded && key === "DATE" && (
                                            <div className="pl-[68px] pr-5 pb-5">
                                                <PhoneHQDatepicker
                                                    key={datepickerKey}
                                                    defaultOption={draftDate ? "" : "all-time"}
                                                    defaultDates={draftDate ? [draftDate.from, draftDate.to] : undefined}
                                                    onDateConfirm={handleDateConfirm}
                                                    trigger={
                                                        <button className="flex items-center gap-2 w-[280px] h-[36px] px-3 border border-fill-subtle rounded-control text-footnote font-medium text-ink hover:border-brand cursor-pointer bg-surface">
                                                            <Calendar size={16} className="!p-0 text-ink-muted"/>
                                                            <span className="flex-1 text-left truncate">{dateLabel}</span>
                                                        </button>
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-line bg-canvas">
                            {hasAnySelection ? (
                                <PhoneHQButton variant="secondary" onClick={handleClear}>
                                    Clear
                                </PhoneHQButton>
                            ) : <span/>}
                            <div className="flex items-center gap-3">
                                <PhoneHQButton variant="secondary" onClick={handleCancel}>
                                    Cancel
                                </PhoneHQButton>
                                <PhoneHQButton variant="primary" onClick={handleApply}>
                                    Apply
                                </PhoneHQButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
